import React, { useState } from 'react';
import { EnvelopeIcon, TrashIcon, PaperClipIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import CustomTooltipComponent from './custom_tooltip_component';
import FilePreviewModal from './preview_modal';
import ListBoxPageSize from './listbox_pase_size';
import PrettyPagination from './pretty_pagination';

export default function ComunicationsList({
    messages,
    showDetails,
    handleShowDetails,
    handleDeleteAlert,
    handleToggleReadBy,
    readByVisible,
    selectedAlertId,
    readByData,
    loadingReadBy,
    errorLoadingReadby,
    currentPageReadby,
    totalPagesReadby,
    handlePreviousPageReadby,
    handleNextPageReadby,
    currentPage,
    totalPages,
    totalCount,
    searchQuery,
    handleSearchChange,
    attachmentFilter,
    handleAttachmentFilterChange,
    pageSize,
    handlePageSizeChange,
    handlePageChange,
}) {
    const [previewDoc, setPreviewDoc] = useState(null);

    const handlePreviewAttachment = (event, attachment) => {
        event.stopPropagation();

        setPreviewDoc({
            name: attachment.file_name || 'Allegato',
            url: attachment.file,
        });
    };

    return (
        <>
            <div className="border-t border-x border-gray-200 bg-gray-50 p-4 pb-6">
                <div className="flex flex-col gap-4">
                    {/* Prima riga: ricerca + risultati per pagina */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="relative w-full sm:max-w-md">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />

                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(event) => handleSearchChange(event.target.value)}
                                placeholder="Cerca comunicazione per titolo..."
                                className="block w-full rounded-lg border-0 bg-white py-2 pl-10 pr-10 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-300"
                            />

                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => handleSearchChange('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            )}
                        </div>

                        <ListBoxPageSize
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        />
                    </div>

                    {/* Seconda riga: filtro allegati */}
                    <div className="flex items-center gap-2 border-t border-gray-200 pt-3">
                        <span className="whitespace-nowrap text-sm text-gray-700">
                            Allegati
                        </span>

                        <div className="inline-flex w-fit overflow-hidden rounded-lg border border-gray-200 bg-white">
                            {[
                                { value: 'all', label: 'Tutti' },
                                { value: 'with', label: 'Con allegati' },
                                { value: 'without', label: 'Senza allegati' },
                            ].map((option, index) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleAttachmentFilterChange(option.value)}
                                    className={`px-3 py-2 text-xs font-semibold transition-colors ${
                                        index > 0 ? 'border-l border-gray-300' : ''
                                    } ${
                                        attachmentFilter === option.value
                                            ? 'bg-indigo-200 text-indigo-700'
                                            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {messages.length === 0 ? (
                <div className="rounded-b-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
                    <EnvelopeIcon className="mx-auto h-10 w-10 text-gray-300" />

                    <p className="mt-3 text-sm font-semibold text-gray-700">
                        Nessuna comunicazione trovata
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                        Prova a modificare la ricerca o il filtro degli allegati
                    </p>
                </div>
            ) : (
                <>
                    <div className="overflow-hidden rounded-b-lg border border-gray-200 bg-white">
                        <ul role="list" className="divide-y divide-gray-200">
                            {messages.map((message) => (
                                <li key={message.id}>
                                    <div>
                                        <div
                                            className="group flex cursor-pointer items-center gap-3 px-4 py-4 transition-colors hover:bg-indigo-50"
                                            onClick={() => handleShowDetails(message.id)}
                                        >
                                            <div className="flex-shrink-0">
                                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-200">
                                                    <EnvelopeIcon className="h-5 w-5 text-indigo-700" />
                                                </span>
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                                                    <div className="flex min-w-0 items-center gap-2">
                                                        <p className="truncate text-sm uppercase text-gray-700">
                                                            {message.title}
                                                        </p>

                                                        {message.attachments &&
                                                            Array.isArray(message.attachments) &&
                                                            message.attachments.length > 0 && (
                                                                <CustomTooltipComponent
                                                                    title="Sono presenti allegati"
                                                                    placement="top-start"
                                                                >
                                                                    <div className="flex flex-shrink-0 items-center">
                                                                        <PaperClipIcon className="h-4 w-4 text-indigo-600" />
                                                                    </div>
                                                                </CustomTooltipComponent>
                                                            )}
                                                    </div>

                                                    <time
                                                        dateTime={message.created_at}
                                                        className="flex-shrink-0 text-xs text-gray-500 sm:text-sm"
                                                    >
                                                        {moment(message.created_at).format('DD/MM/YYYY HH:mm')}
                                                    </time>
                                                </div>
                                            </div>

                                            <CustomTooltipComponent
                                                title="Elimina avviso"
                                                placement="top-start"
                                            >
                                                <button
                                                    type="button"
                                                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleDeleteAlert(
                                                            message.id,
                                                            message.title
                                                        );
                                                    }}
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </CustomTooltipComponent>
                                        </div>

                                        {showDetails[message.id] && (
                                            <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 sm:pl-16">
                                                <div className="pr-0 sm:pr-4">
                                                    <span className="text-xs uppercase tracking-wider text-gray-500">
                                                        Messaggio
                                                    </span>

                                                    <div className="mt-2 max-h-40 overflow-y-auto text-sm text-gray-800">
                                                        {message.message
                                                            .split('\n')
                                                            .map((line, index) => (
                                                                <p
                                                                    key={index}
                                                                    className="break-words"
                                                                >
                                                                    {line}
                                                                </p>
                                                            ))}
                                                    </div>

                                                    {message.attachments &&
                                                        Array.isArray(message.attachments) &&
                                                        message.attachments.length > 0 && (
                                                            <div className="mt-4 border-t border-gray-200 pt-4">
                                                                <span className="text-xs uppercase tracking-wider text-gray-500">
                                                                    Allegati
                                                                </span>

                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                    {message.attachments.map(
                                                                        (attachment) => (
                                                                            <button
                                                                                key={attachment.id}
                                                                                type="button"
                                                                                onClick={(event) =>
                                                                                    handlePreviewAttachment(
                                                                                        event,
                                                                                        attachment
                                                                                    )
                                                                                }
                                                                                className="flex max-w-full items-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-indigo-600 shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-800"
                                                                            >
                                                                                <PaperClipIcon className="mr-2 h-4 w-4 flex-shrink-0" />

                                                                                <span className="truncate">
                                                                                    {attachment.file_name ||
                                                                                        'Allegato'}
                                                                                </span>
                                                                            </button>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                    <div className="mt-4 border-t border-gray-200 pt-4">
                                                        <span className="text-xs uppercase tracking-wider text-gray-500">
                                                            Inviato a
                                                        </span>

                                                        <div className="mt-2 text-sm text-gray-800">
                                                            {message.organizational_units && Array.isArray(message.organizational_units) && message.organizational_units.length > 0 ? (
                                                                <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                                                                    {message.organizational_units_details &&
                                                                    Array.isArray(message.organizational_units_details) ? (
                                                                        message.organizational_units_details.map(
                                                                            (organizationalUnit) => (
                                                                                <li key={organizationalUnit.id} className="flex items-center text-sm text-gray-800">
                                                                                    <span className="mr-2 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-600" />
                                                                                    Unità organizzativa:{' '}{organizationalUnit.name}
                                                                                </li>
                                                                            )
                                                                        )
                                                                    ) : (
                                                                        <li className="text-sm text-gray-500">
                                                                            Nessuna unità organizzativa trovata
                                                                        </li>
                                                                    )}
                                                                </ul>
                                                            ) : message.send_to_all ? (
                                                                <div>
                                                                    <span className="font-medium">Tutti</span>

                                                                    {message.employees_details &&
                                                                        Array.isArray(message.employees_details) &&
                                                                        message.employees_details.length > 0 && (
                                                                            <div className="mt-2 max-h-40 overflow-y-auto">
                                                                                <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                                                                                    {message.employees_details
                                                                                        .slice()
                                                                                        .sort((a, b) => (a.cf || '').localeCompare(b.cf || ''))
                                                                                        .map((employee) => (
                                                                                            <li
                                                                                                key={employee.id}
                                                                                                className="flex items-center text-xs text-gray-800"
                                                                                            >
                                                                                                <span className="mr-2 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-600" />
                                                                                                {employee.last_name && employee.first_name
                                                                                                    ? `${employee.last_name} ${employee.first_name}`
                                                                                                    : employee.cf}
                                                                                            </li>
                                                                                        ))}
                                                                                </ul>
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            ) : message.employees && Array.isArray(message.employees) && message.employees.length > 0 ? (
                                                                <div className="max-h-40 overflow-y-auto">
                                                                    <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                                                                        {message.employees_details && Array.isArray(message.employees_details) ? (
                                                                            message.employees_details
                                                                                .slice()
                                                                                .sort((a, b) => a.cf.localeCompare(b.cf))
                                                                                .map((employee) => (
                                                                                        <li key={employee.id} className="flex items-center text-xs text-gray-800">
                                                                                            <span className="mr-2 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-600" />
                                                                                            {employee.last_name && employee.first_name
                                                                                                ? `${employee.last_name} ${employee.first_name}`
                                                                                                : employee.cf}
                                                                                        </li>
                                                                                    )
                                                                                )
                                                                        ) : (
                                                                            <li className="text-xs text-gray-500">
                                                                                Nessun dipendente trovato
                                                                            </li>
                                                                        )}
                                                                    </ul>
                                                                </div>
                                                            ) : (
                                                                <span>Nessuno</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 border-t border-gray-200 pt-4">
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-xs uppercase tracking-wider text-gray-500">
                                                                Letto da:{' '}
                                                                <span className="font-semibold text-gray-700">
                                                                    {message?.read_by}
                                                                </span>
                                                            </span>

                                                            {Number(message?.read_by) > 0 && (
                                                                <button
                                                                    type="button"
                                                                    className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
                                                                    onClick={() => handleToggleReadBy(message.id)}
                                                                >
                                                                    {readByVisible && selectedAlertId == message.id ? 'Nascondi' : 'Visualizza'}
                                                                </button>
                                                            )}
                                                        </div>

                                                        {loadingReadBy && selectedAlertId == message.id && (
                                                            <p className="mt-2 text-sm text-gray-500">Caricamento...</p>
                                                        )}

                                                        {errorLoadingReadby && selectedAlertId == message.id && (
                                                            <p className="mt-2 text-sm text-gray-600">⚠️ Errore nel recupero dei dati.</p>
                                                        )}

                                                        {!loadingReadBy && !errorLoadingReadby && readByVisible && selectedAlertId == message.id && (
                                                            <div className="mt-4 rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                                                                <h3 className="mb-2 text-sm font-semibold text-gray-800">
                                                                    Dipendenti
                                                                </h3>

                                                                <ul className="max-h-60 space-y-1 overflow-y-auto text-sm text-gray-700">
                                                                    {readByData.map(
                                                                        ({employee, read_at}, index) => (
                                                                            <li key={index} className="border-b border-gray-100 pb-1">
                                                                                {employee.last_name}{' '}{employee.first_name}{' '}–{' '}
                                                                                <span className="text-xs text-gray-500">
                                                                                    {moment(read_at).format('DD/MM/YYYY')}{' '}-{' '}{moment(read_at).format('HH:mm:ss')}
                                                                                </span>
                                                                            </li>
                                                                        )
                                                                    )}
                                                                </ul>

                                                                {readByData.length > 0 && totalPagesReadby > 1 && (
                                                                    <div className="mt-4 flex items-center justify-between gap-4">
                                                                        <button
                                                                            type="button"
                                                                            onClick={handlePreviousPageReadby}
                                                                            disabled={currentPageReadby === 1}
                                                                            className={`rounded-md border px-2 py-1 text-sm font-semibold shadow-sm ${
                                                                                currentPageReadby === 1 ? 'cursor-not-allowed text-gray-400' : 'text-gray-700 hover:bg-gray-100'
                                                                            }`}
                                                                        >
                                                                            Pagina precedente
                                                                        </button>

                                                                        <div className="text-center text-sm font-semibold text-gray-700">
                                                                            Pagina{' '}{currentPageReadby}{' '}
                                                                            di{' '}{totalPagesReadby}
                                                                        </div>

                                                                        <button
                                                                            type="button"
                                                                            onClick={handleNextPageReadby}
                                                                            disabled={currentPageReadby === totalPagesReadby}
                                                                            className={`rounded-md border px-2 py-1 text-sm font-semibold shadow-sm ${
                                                                                currentPageReadby === totalPagesReadby
                                                                                    ? 'cursor-not-allowed text-gray-400'
                                                                                    : 'text-gray-700 hover:bg-gray-100'
                                                                            }`}
                                                                        >
                                                                            Pagina successiva
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <PrettyPagination
                        page={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        onChange={handlePageChange}
                        labelSingolare="comunicazione"
                        labelPlurale="comunicazioni"
                        className="py-3 border border-t rounded-lg mt-3 border-gray-200 bg-gray-50"
                    />
                </>
            )}

            <FilePreviewModal
                doc={previewDoc}
                onClose={() => setPreviewDoc(null)}
            />
        </>
    );
}
